import axios from 'axios';
import Papa from 'papaparse';
import { alkymetricsClient } from '../alkymetricsClient';
import { screeningsClient } from '../screeningsClient';
import { alkymindClient } from '../alkymindClient';
import { getLocalStorageWorkspace } from '../../utils/workspaceUtils';
import {
  CHAT_ASSISTANT_ENDPOINTS,
  CHAT_ASSISTANT_DEFAULTS,
  CHAT_ASSISTANT_MESSAGES,
  FORMDATA_HEADERS,
  ERROR_STATUSES,
} from '../../constants/chatAssistant.constants';
import { t } from '../../i18n/i18n';

export const getScreenings = async (workspaceId) => {
  try {
    const { data } = await screeningsClient.get(`/screenings/workspace?workspace=${workspaceId}`);

    return data;
  } catch (err) {
    return [];
  }
};

export const getScreeningsPaginated = async (params) => {
  try {
    const response = await screeningsClient.get(`/screenings/filters`, {
      params: { ...params, paginated: true },
    });

    return response?.data;
  } catch (err) {
    return { collection: [], pagination: null };
  }
};

export const createScreeningInvitations = async (body) => {
  const { data } = await alkymetricsClient.post('/assessmentInvitation/batch', body);
  return data;
};

export const getPsychotechnicByScreeningInstanceId = async (screeningInstanceId = '') => {
  const { data } = await screeningsClient.get(
    `/psychotechnics/screeningInstance/${screeningInstanceId}`
  );
  return data;
};

export const getScreeningsInvitations = async ({
  search,
  orderBy,
  status,
  processStatus,
  screening,
  offset = 0,
  limit = 15,
  order,
  stage,
  sex,
  minScore,
  maxScore,
  academicLevel,
  country,
  languages,
  visible,
  state,
  minEvents,
  maxEvents,
  qualitativeResultsIds,
  qualitativeAxisIds,
  reviewType,
}) => {
  const { data } = await alkymetricsClient.get('/assessmentInvitation', {
    params: {
      search,
      orderBy,
      status,
      processStatus,
      screening,
      offset,
      limit,
      order,
      stage,
      sex,
      minScore,
      maxScore,
      academicLevel,
      country,
      languages,
      state,
      minEvents,
      maxEvents,
      qualitativeResultsIds,
      visible,
      qualitativeAxisIds,
      reviewType,
    },
  });

  return data;
};

export const getScreeningInvitationsStatsCount = async (recruitSessionStageId) => {
  const { data } = await alkymetricsClient.get(
    `/assessmentInvitation/statusCount/${recruitSessionStageId}`
  );

  return data;
};

export const updateProctoringEnabledRecruitSessionStage = async (recruitSessionStageId, body) => {
  const url = `/recruitSessionStage/proctoringEnabled/${recruitSessionStageId}`;
  const { data } = await alkymetricsClient.patch(url, body);
  return data;
};
export const updateRecruitSessionStage = async (recruitSessionStageId, body) => {
  const url = `/recruitSessionStage/${recruitSessionStageId}`;
  const { data } = await alkymetricsClient.patch(url, body);
  return data;
};

export const updateScreeningCandidatesBatch = async ({
  ids,
  processStatus,
  allCandidates,
  deletedByPartner,
  filters,
  toggleVisibility,
  limitDate,
}) =>
  alkymetricsClient.post('/assessmentInvitation/batch-edit', {
    ids,
    processStatus,
    allCandidates,
    deletedByPartner,
    filters,
    toggleVisibility,
    limitDate,
  });

export const getCandidatesGeneralDashboard = async (data, params) =>
  screeningsClient.post('/users/summary', data, { params });

export const createCustomSearch = async (searchData) => {
  try {
    const { data } = await alkymetricsClient.post('/recruitSession', searchData);
    return data;
  } catch (error) {
    return {};
  }
};

export const getCustomSearch = async (id) => {
  try {
    const { data } = await alkymetricsClient.get(`/recruitSession/${id}`);
    return data;
  } catch (error) {
    return {};
  }
};

export const getCustomSearchs = async ({ offset = 0, active, search, limit = 6 }) => {
  try {
    const { data } = await alkymetricsClient.get(`/recruitSession`, {
      params: {
        offset,
        ...(active !== '' && { active }),
        search,
        limit,
      },
    });
    return data;
  } catch (error) {
    return {};
  }
};

export const getAllCustomSearchs = async (filters) => {
  try {
    const { data } = await alkymetricsClient.get(`/recruitSession/all`, {
      params: {
        ...filters,
      },
    });
    return data;
  } catch (error) {
    console.error('Error fetching custom searches:', error);
    return [];
  }
};

export const getACustomSearchsById = async (id) => {
  try {
    const { data } = await alkymetricsClient.get(`/recruitSession/${id}`);
    return data;
  } catch (error) {
    console.error('Error fetching custom searches:', error);
    return [];
  }
};

export const getPinnedRecruitSessions = async (filters) => {
  try {
    const { data } = await alkymetricsClient.get(`/recruitSession/pinnedRecruitSessions`, {
      params: {
        ...filters,
      },
    });
    return data;
  } catch (error) {
    return [];
  }
};

export const getFeaturedRecruitSessions = async (filters, signal = null) => {
  try {
    const { data } = await alkymetricsClient.get(`/recruitSession/featured`, {
      params: {
        ...filters,
      },
      signal,
    });
    return data;
  } catch (error) {
    throw error;
  }
};

export const updatePinnedRecruitSessions = async (filters) => {
  try {
    const { partnerId, pinnedRecruitSessions } = filters;
    if (!partnerId || !pinnedRecruitSessions) {
      return null;
    }
    const { data } = await alkymetricsClient.patch(`/partner-to-workspace/${partnerId}`, {
      pinnedRecruitSessions,
    });
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

export const getAllWorkspaces = async ({ offset = 0, limit = 12, search, owner }) => {
  try {
    const { data } = await alkymetricsClient.get(`/workspace/all`, {
      params: { offset, limit, search, owner },
    });
    return data;
  } catch (error) {
    return [];
  }
};

export const getWorkspaceOwners = async () => {
  try {
    const { data } = await alkymetricsClient.get('/workspace/owners');
    return data;
  } catch (error) {
    return [];
  }
};

export const setFavoriteWorkspace = async (body) => {
  try {
    const { data } = await alkymetricsClient.patch('/workspace/favorite', body);
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

export const leaveWorkspace = async (workspaceId) => {
  try {
    const { data } = await alkymetricsClient.post(`/workspace/leave/${workspaceId}`);
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

export const getDraft = async ({ offset = 0, search, limit = 6, workspaceIds }) => {
  try {
    const { data } = await screeningsClient.get(`/recruitsessiondraft`, {
      params: {
        offset,
        name: search,
        limit,
        workspaceIds,
      },
    });
    return data;
  } catch (error) {
    return {};
  }
};

export const deleteDraft = async (draftId) => {
  try {
    const { data } = await screeningsClient.delete(`/recruitsessiondraft/${draftId}`);
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

export const getCustomSearchStageInvitations = async (id, query) => {
  try {
    const { data } = await alkymetricsClient.get(`/recruitSessionStage/${id}`, {
      params: {
        ...query,
      },
    });
    return data;
  } catch (error) {
    return {};
  }
};

export const exportCsvInvitations = async (id, query, lang) => {
  try {
    const { data } = await alkymetricsClient.get(`/recruitSessionStage/${id}/export`, {
      params: {
        ...query,
        exportLanguage: lang,
      },
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    return { error: 'EXPORT_ERROR' };
  }
};

export const getCustomSearchStageInvitationsData = async (id) => {
  try {
    const { data } = await alkymetricsClient.get(`/recruitSessionStage/${id}/invitations/data`);
    return data;
  } catch (error) {
    return {};
  }
};

export const updateRecruitSession = async (id, dataToUpdate) => {
  try {
    const { data } = await alkymetricsClient.patch(`/recruitSession/${id}`, dataToUpdate);
    return data;
  } catch (error) {
    return {};
  }
};

export const getInvitationComments = async (id, recruitSessionId, params) => {
  try {
    const { data } = await alkymetricsClient.get(
      `/recruitSessionComment/recruitSession/${recruitSessionId}/profile/${id}`,
      {
        params,
      }
    );
    return data;
  } catch (error) {
    return [];
  }
};

export const uploadCommentFile = async (file, recruitSessionId, profileId) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await alkymetricsClient.post(
      `/recruitSessionComment/upload-file?recruitSessionId=${recruitSessionId}&profileId=${profileId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  } catch (error) {
    console.error('Error uploading comment file:', error);
    throw error;
  }
};

export const createComment = async (commentData) => {
  try {
    const { file, ...restData } = commentData;

    let fileData = null;

    // First upload the file if present
    if (file) {
      fileData = await uploadCommentFile(file, restData.recruitSessionId, restData.profileId);
    }

    // Then create the comment with the file info
    const commentPayload = {
      ...restData,
      ...(fileData && { file: fileData }),
    };

    const { data } = await alkymetricsClient.post('/recruitSessionComment', commentPayload);
    return data;
  } catch (error) {
    return {};
  }
};

export const deleteComment = async (id) => {
  try {
    const { data } = await alkymetricsClient.delete(`/recruitSessionComment/${id}`);
    return data;
  } catch (error) {
    return {};
  }
};

export const passStageInBatch = async (body) => {
  try {
    const { data } = await alkymetricsClient.post('/assessmentInvitation/passStage/batch', body);
    return data;
  } catch (error) {
    return null;
  }
};

export const sendInvitationEmailInBatch = async (body) => {
  try {
    await alkymetricsClient.post('/assessmentInvitation/sendEmail/batch', body);
    return true;
  } catch (error) {
    return false;
  }
};

export const getSearchesQuantity = async () => {
  try {
    const { data } = await alkymetricsClient.get('/recruitSession/quantity');
    return data;
  } catch (error) {
    return null;
  }
};

export const getTestGroups = async (ids) => {
  if (!ids?.length) return [];
  try {
    const { data } = await screeningsClient.get(`/screenings/testGroups`, {
      params: {
        screeningsInstances: ids.toString(),
      },
    });
    return data;
  } catch (error) {
    return [];
  }
};

export const getTestGroupsDetailsBatch = async (ids) => {
  if (!ids?.length) return [];
  try {
    const { data } = await screeningsClient.get(`/testGroups/batch`, {
      params: {
        ids: ids.toString(),
      },
    });
    return data;
  } catch (error) {
    return [];
  }
};

export const getRecommendedTestgroups = async (ids) => {
  if (!ids?.length) return [];
  try {
    const { data } = await screeningsClient.get(`/testGroups/recommendedTestgroups`, {
      params: {
        grouperKeys: ids.toString(),
      },
    });
    return data;
  } catch (error) {
    return [];
  }
};

export const getTags = async ({ types }) => {
  try {
    const { data } = await screeningsClient.get(`/tags`, {
      params: {
        types,
      },
    });
    return data;
  } catch (error) {
    return [];
  }
};
export const getEndorsements = async (request) => {
  try {
    const { data } = await screeningsClient.get(`/endorsement`, {
      params: request,
    });
    return data;
  } catch (error) {
    return [];
  }
};

export const getCategories = async () => {
  try {
    const { data } = await screeningsClient.get(`/categories`);
    return data;
  } catch (error) {
    return [];
  }
};
export const getCategoriesAll = async () => {
  try {
    const { data } = await screeningsClient.get(`/categories/all`);
    return data;
  } catch (error) {
    return [];
  }
};

export const updateCategories = async (id, body, newFile = null) => {
  try {
    const formData = new FormData();
    Object.entries(body).forEach(([key, value]) => {
      if (Array.isArray(value) || typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });
    formData.append('file', newFile);

    const { data } = await screeningsClient.patch(`/categories/${id}`, formData, {
      'Content-Type': 'multipart/form-data',
    });
    return data;
  } catch (error) {
    return null;
  }
};

export const createCategory = async (body, newFile) => {
  try {
    const formData = new FormData();
    Object.entries(body).forEach(([key, value]) => {
      if (Array.isArray(value) || typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });
    formData.append('file', newFile);

    const { data } = await screeningsClient.post(`/categories`, formData, {
      'Content-Type': 'multipart/form-data',
    });
    return data;
  } catch (error) {
    return null;
  }
};

export const deleteCategory = async (id) => {
  try {
    const { data } = await screeningsClient.delete(`/categories/${id}`);
    return data;
  } catch (error) {
    return null;
  }
};

export const getCategoriesWithTestGroups = async () => {
  try {
    const { data } = await screeningsClient.get(`/categories/withTestGroups`);
    return data;
  } catch (error) {
    return [];
  }
};

export const getSubskills = async () => {
  try {
    const { data } = await screeningsClient.get(`/subskills`);
    return data;
  } catch (error) {
    return [];
  }
};

export const getSubskillsAll = async () => {
  try {
    const { data } = await screeningsClient.get(`/subskills/all`);
    return data;
  } catch (error) {
    return [];
  }
};

export const updateSubskills = async (id, body, newFile = null) => {
  try {
    const formData = new FormData();
    Object.entries(body).forEach(([key, value]) => {
      if (Array.isArray(value) || typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });
    formData.append('file', newFile);

    const { data } = await screeningsClient.patch(`/subskills/${id}`, formData);
    return data;
  } catch (error) {
    return null;
  }
};

export const createSubskill = async (body, newFile) => {
  try {
    const formData = new FormData();
    Object.entries(body).forEach(([key, value]) => {
      if (Array.isArray(value) || typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });
    formData.append('file', newFile);

    const { data } = await screeningsClient.post(`/subskills`, formData);
    return data;
  } catch (error) {
    return null;
  }
};

export const deleteSubskill = async (id) => {
  try {
    const { data } = await screeningsClient.delete(`/subskills/${id}`);
    return data;
  } catch (error) {
    return null;
  }
};

export const getRolesFilters = async () => {
  try {
    const { data } = await screeningsClient.get(`/roles`);
    return data;
  } catch (error) {
    return [];
  }
};

export const getRolesAll = async () => {
  try {
    const { data } = await screeningsClient.get(`/roles/all`);
    return data;
  } catch (error) {
    return [];
  }
};

export const updateRoles = async (id, body) => {
  try {
    const { data } = await screeningsClient.patch(`/roles/${id}`, body);
    return data;
  } catch (error) {
    return null;
  }
};

export const createRol = async (body) => {
  try {
    const { data } = await screeningsClient.post(`/roles`, body);
    return data;
  } catch (error) {
    return null;
  }
};

export const deleteRol = async (id) => {
  try {
    const { data } = await screeningsClient.delete(`/roles/${id}`);
    return data;
  } catch (error) {
    return null;
  }
};

export const getRolesWithTestGropusFilters = async () => {
  try {
    const { data } = await screeningsClient.get(`/roles/withTestGroups`);
    return data;
  } catch (error) {
    return [];
  }
};

export const getQuickRolesWithTestGroups = async () => {
  try {
    const { data } = await screeningsClient.get(`/quick-role/withTestGroups`);
    return data;
  } catch (error) {
    return [];
  }
};

export const getScreeningTestGroups = async ({
  offset = 0,
  limit = 15,
  tagsIds,
  categoryId,
  name,
  favorites,
  rolesIds,
  recommendTestGroups,
  testgroupsIds,
  lang = 'es',
}) => {
  try {
    const { data } = await screeningsClient.get('/testGroups', {
      params: {
        offset,
        limit,
        tagsIds,
        categoryId,
        text: name,
        favorites,
        rolesIds,
        recommendTestGroups,
        searchInDescription: true,
        metrics: true,
        testgroupsIds,
        lang,
      },
    });
    return {
      collection: data.collection.map((data) => ({ ...data, isSelected: false })),
      pagination: data.pagination,
    };
  } catch (error) {
    return [];
  }
};

export const postScreening = async (screeningData) => {
  try {
    const { data } = await screeningsClient.post('/screenings', screeningData);
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

export const addTestGroupFavorite = async (favoriteData) => {
  try {
    const { data } = await screeningsClient.post('/testGroupsFavorites/create', favoriteData);
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

export const removeTestGroupFavorite = async (testGroupId) => {
  try {
    const { data } = await screeningsClient.delete(`/testGroupsFavorites/${testGroupId}`);
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

export const postTestGroup = async (testGroupData) => {
  try {
    const { data } = await screeningsClient.post('/testGroups/create', testGroupData);
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

export const patchTestGroup = async (testGroupData, replaceFlag) => {
  try {
    const { data } = await screeningsClient.patch(
      `/testGroups?replaceFlag=${replaceFlag}`,
      testGroupData
    );
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

export const patchScreeningAlkemy = async (challengeData) => {
  try {
    const { data } = await screeningsClient.patch(`/screenings/alkemy`, challengeData);
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

export const postSendEmailCustom = async (body) => {
  const { data } = await alkymetricsClient.post('/email/partner/custom', body);
  return data;
};

export const postSendCustomEmailByWorkspace = async (body) => {
  const { data } = await alkymetricsClient.post('/email/custom/byWorkspace', body);
  return data;
};

export const getSubmissionAnswers = async (screeningInstanceId, testGroupId) => {
  try {
    const { data } = await screeningsClient.get(
      `/submissions/${screeningInstanceId}/${testGroupId}`
    );
    return data;
  } catch (error) {
    return [];
  }
};
export const getTestGroup = async (testGroupId, sample = false) => {
  try {
    const companyId = getLocalStorageWorkspace()?.companyId;
    const { data } = await screeningsClient.get(
      `/testGroups/${testGroupId}?sample=${sample}&companyId=${companyId}`
    );
    return data;
  } catch (error) {
    return [];
  }
};

export const getTestGroupAllTests = async (testGroupId) => {
  try {
    const { data } = await screeningsClient.get(`/testGroups/randomComplete/${testGroupId}`);
    return data;
  } catch (error) {
    return [];
  }
};

export const getScreeningAllTests = async (screeningId) => {
  try {
    const { data } = await screeningsClient.get(`/screenings/complete/${screeningId}`);
    return data;
  } catch (error) {
    return [];
  }
};

export const getScreeningByInstanceId = async (screeningInstanceId) => {
  const { data } = await screeningsClient.get(`/screenings/instance/${screeningInstanceId}`);
  return data;
};

export const getCompanyScreenings = async (query = {}) => {
  const { data } = await screeningsClient.get('/screenings/alkemy', {
    params: {
      ...query,
    },
  });
  return data;
};

export const getCompanyTestgroups = async (query = {}) => {
  const { data } = await screeningsClient.get('/testGroups/all', {
    params: {
      ...query,
      metrics: true,
    },
  });

  return data;
};

export const getAutoSelectTestgroups = async (query = {}) => {
  const { data } = await screeningsClient.get('/testGroups/autoselect', {
    params: {
      ...query,
      metrics: true,
    },
  });

  return data;
};

export const editTestgroup = async (options = {}) => {
  const { data } = await screeningsClient.patch(`/testgroups?replaceFlag=false`, options);
  return data;
};

export const editScreening = async (id, options = {}) => {
  const { data } = await screeningsClient.patch(`/screenings/screening/${id}`, options);
  return data;
};

export const getLevels = async () => {
  const { data } = await screeningsClient.get('/level/levels');
  return data;
};

export const createScreeningAlkemy = async (screeningValues = {}) => {
  const { data } = await screeningsClient.post('/screenings/alkemy', screeningValues);
  return data;
};

export const getImageSignedUrl = async (mimeType) => {
  const { data } = await screeningsClient.get('/tests/upload/image', {
    params: {
      mimeType,
    },
  });
  return data;
};

export const getAudioSignedUrl = async (mimeType) => {
  const { data } = await screeningsClient.get('/tests/upload/audio', {
    params: {
      mimeType,
    },
  });
  return data;
};

export const getPdfSignedUrl = async (mimeType) => {
  const { data } = await screeningsClient.get('/tests/upload/pdf', {
    params: {
      mimeType,
    },
  });
  return data;
};

export const uploadFileToS3 = async (imageUrl, data, mimeType) => {
  const { res } = await axios.put(imageUrl, data, {
    headers: {
      'Content-Type': mimeType,
    },
    data,
  });
  return res;
};

export const uploadVideoFileToS3 = async (videoUrl, data) => {
  const { res } = await axios.put(videoUrl, Buffer.from(data), {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
  });
  return res;
};

export const getTestDifficulty = async () => {
  try {
    const { data } = await screeningsClient.get(`/testDifficulty`);
    return data;
  } catch (err) {
    return [];
  }
};

export const getLangCodesAI = async (preLangCodesAvailable = []) => {
  try {
    const queryParams =
      preLangCodesAvailable.length > 0 ? `?ids=${preLangCodesAvailable.join(',')}` : '';

    const { data } = await screeningsClient.get(`/langCodesAI${queryParams}`);
    return data;
  } catch (err) {
    return [];
  }
};

export const getPromptCorrectionTextAI = async (testGroupABM) => {
  try {
    const { data } = await screeningsClient.get(
      `/questionTypeConfig/prompting/all?testGroupABM=${testGroupABM}&answerType=TEXT_AI`
    );
    return data;
  } catch (err) {
    return [];
  }
};

export const getStageScreening = async ({ companyId, levelId }) => {
  const { data } = await screeningsClient.get(`/screenings/random`, {
    params: {
      companyId,
      levelId,
    },
  });
  return data;
};

export const getScreeningDetails = async (screeningId) => {
  const { data } = await screeningsClient.get(`/screenings/${screeningId}`);
  return data;
};

export const getRestartScreeningData = async ({ levelId, companyId }) => {
  const { data } = await screeningsClient.get('/screenings/restartdata', {
    params: {
      levelId,
      companyId,
    },
  });
  return data;
};

export const postRestartTestgroup = async (body) => {
  const { data } = await screeningsClient.post('/screenings/restart/testgroup', body);
  return data;
};

export const postRecruitSessionDraft = async (body) => {
  const { data } = await screeningsClient.post('/recruitsessiondraft', body);
  return data;
};

export const editRecruitSessionDraft = async (draftId, body) => {
  const { data } = await screeningsClient.patch(`/recruitsessiondraft/${draftId}`, body);
  return data;
};

export const getRecruitSessionDraft = async (draftId) => {
  const { data } = await screeningsClient.get(`/recruitsessiondraft/${draftId}`);
  return data;
};

export const openEvaluation = async (body) => {
  const { data } = await screeningsClient.post('/recruitsessiondraft/opendraft', body);
  return data;
};

export const duplicateRecruitSessionDraft = async (body) => {
  const { data } = await screeningsClient.post('/recruitsessiondraft/duplicate', body);
  return data;
};

export const getRecruitSessionMetricsById = async (id) => {
  try {
    const { data } = await screeningsClient.get(`/metrics/stage-metric/${id}`);
    return data;
  } catch (error) {
    return null;
  }
};

export const createWorkspaceAsync = async (body) => {
  try {
    const { data } = await alkymetricsClient.post('/workspace/create', body);
    return data;
  } catch (error) {
    return null;
  }
};

export const getAIQuestionTrial = async (body) => {
  try {
    const { data } = await alkymindClient.post('/ai/tests/text/open', body);
    if (data?.error) throw new Error(data?.error);
    return data?.result;
  } catch (error) {
    throw new Error(error);
  }
};

export const getAISuggestions = async (body) => {
  try {
    const { data } = await alkymindClient.post('/ai/tests/suggestion', body);
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

export const getUserChallengeCertificates = async (userId, token = null) => {
  const { data } = await screeningsClient.get(
    `/screenings/instances/certificates/${userId}?visible=true`,
    token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : {}
  );
  return data;
};

export const getTestFormats = async () => {
  const data = await screeningsClient.get('/testformat');
  return data?.data;
};

export const transferScreeningDraft = async (draftId, screeningData) => {
  const data = await screeningsClient.post(
    `/recruitsessiondraft/transfer/${draftId}`,

    screeningData
  );
  return data?.data;
};

export const searchInPrefilterQuesitons = async (companyId, workspaceId, text) => {
  try {
    const { data } = await screeningsClient.get('/prefilterQuestions/search', {
      companyId,
      workspaceId,
      text,
    });
    return data;
  } catch (error) {
    return [];
  }
};

export const getPrefilterQuesitons = async ({ companyId, limit = 20, offset = 0 }) => {
  try {
    const { data } = await screeningsClient.get('/prefilterQuestions/all', {
      params: { companyId, limit, offset },
    });
    return data;
  } catch (error) {
    return [];
  }
};

export const deletePrefilterQuesiton = async (prefilterQuestionId) => {
  const { data } = await screeningsClient.delete(`/prefilterQuestions/test/${prefilterQuestionId}`);
  return data;
};

export const editPrefilterQuestion = async (prefilterQuestion) => {
  const { data } = await screeningsClient.patch(
    `/prefilterQuestions/question/${prefilterQuestion.testId ?? prefilterQuestion.id}`,
    prefilterQuestion
  );
  return data;
};

export const postRestartPrefilterTest = async (body) => {
  const { data } = await screeningsClient.post('/prefilterQuestions/restartTest', body);
  return data;
};

// Additional Questions Services
export const getAdditionalQuestions = async ({ workspaceId, limit = 20, offset = 0 }) => {
  try {
    const { data } = await screeningsClient.get('/additionalQuestions/all', {
      params: { workspaceId, limit, offset },
    });
    return data;
  } catch (error) {
    return { collection: [], pagination: { limit, offset, total: 0 } };
  }
};

export const searchAdditionalQuestions = async ({ workspaceId, text }) => {
  try {
    const { data } = await screeningsClient.get('/additionalQuestions/search', {
      params: { workspaceId, text },
    });
    return data;
  } catch (error) {
    return [];
  }
};

export const deleteAdditionalQuestion = async (questionId) => {
  const { data } = await screeningsClient.delete(`/additionalQuestions/test/${questionId}`);
  return data;
};

export const editAdditionalQuestion = async (questionId, questionData) => {
  const { data } = await screeningsClient.post(
    `/additionalQuestions/question/${questionId}`,
    questionData
  );
  return data;
};

export const getSearchTypes = async (parentId, roleId = null) => {
  const data = await screeningsClient.get(
    `/searchTypes?parentId=${parentId ?? ''}&roleId=${roleId ?? ''}`
  );
  return data?.data;
};

export const postTestTranslation = async (test) => {
  const data = await screeningsClient.post('/tests/translate', test);
  return data;
};

export const editTestTranslation = async (test) => {
  const data = await screeningsClient.patch('/tests/translate', test);
  return data;
};

export const getAITestTranslation = async (test) => {
  const { data } = await alkymindClient.post('/ai/tests/translation', test);
  return data;
};

export const translateTestgroupLanguage = async (data) => {
  const response = await screeningsClient.patch('/testGroups/translate', data);
  return response;
};

export const getSubSkills = async () => {
  try {
    const { data } = await screeningsClient.get('/subskills/all');
    return data;
  } catch (error) {
    return [];
  }
};

export const getAllTests = async (filters = {}) => {
  try {
    const { data } = await screeningsClient.get('/tests/all', {
      params: filters,
    });
    return data;
  } catch (error) {
    console.error('Error fetching tests:', error);
    throw error;
  }
};

// CV_FILE Prefilter Management Functions
export const downloadCandidateCV = async (candidateId, invitationId) => {
  try {
    const response = await screeningsClient.get(`/candidates/${candidateId}/cv/download`, {
      params: { invitationId },
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error downloading CV:', error);
    throw error;
  }
};

// Manual Review Endpoint for CV_FILE Prefilter
export const handleManualReviewDecision = async (instanceId, decision) => {
  try {
    const response = await screeningsClient.patch(
      `/prefilterQuestions/manual-review/${instanceId}`,
      {
        decision, // 0 = REJECT, 1 = APPROVE
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error handling manual review:', error);
    throw error;
  }
};

export const getTestById = async (testId, lang = 'es') => {
  try {
    const { data } = await screeningsClient.get(`/tests/${testId}`, {
      params: { lang },
    });
    return data;
  } catch (error) {
    console.error('Error fetching test:', error);
    throw error;
  }
};

export const getTestsByIds = async (testIds, lang = 'es') => {
  try {
    const { data } = await screeningsClient.post('/tests/byIds', testIds, {
      params: { lang },
    });
    return data;
  } catch (error) {
    console.error('Error fetching tests by IDs:', error);
    throw error;
  }
};

export const updateTest = async (testId, testData) => {
  try {
    const { data } = await screeningsClient.put(`/tests/${testId}`, testData);
    return data;
  } catch (error) {
    console.error('Error updating test:', error);
    throw error;
  }
};

export const bulkUpdateTests = async (testsData) => {
  try {
    const { data } = await screeningsClient.put('/tests/bulk/update', testsData);
    return data;
  } catch (error) {
    console.error('Error bulk updating tests:', error);
    throw error;
  }
};

export const parseCSVForBulkUpdate = (csvContent) => {
  const parseResult = Papa.parse(csvContent, {
    header: true,
    skipEmptyLines: true,
    trimHeaders: true,
    transform: (value) => value.trim(),
  });

  if (parseResult.errors.length > 0) {
    const errorMessages = parseResult.errors
      .map((error) => `Row ${error.row}: ${error.message}`)
      .join(', ');
    throw new Error(`CSV parsing errors: ${errorMessages}`);
  }

  const { data, meta } = parseResult;

  if (!meta.fields.includes('testId')) {
    throw new Error('CSV must include testId column');
  }

  const tests = data
    .filter((row) => row.testId)
    .map((row) => {
      const test = {
        testId: row.testId,
        data: {},
      };

      Object.entries(row).forEach(([key, value]) => {
        if (key !== 'testId') {
          if (key === 'subSkillIds') {
            test.data[key] = value ? value.split(';').map((id) => id.trim()) : [];
          } else {
            test.data[key] = value;
          }
        }
      });

      return test;
    });

  return { tests };
};

export const createNps = async (npsData) => {
  try {
    const { data } = await screeningsClient.post('/nps/create', npsData);
    return data;
  } catch (error) {
    throw new Error(error);
  }
};

export const getEvaluationAssistantResponse = async (message, threadId, draftId = null) => {
  const { data } = await alkymindClient.post(
    CHAT_ASSISTANT_ENDPOINTS.EVALUATION_ASSISTANT_MESSAGE,
    {
      message,
      threadId,
      draftId,
    }
  );

  return data;
};

export const transcribeAudioForAssistant = async (audioFile) => {
  if (!audioFile) {
    throw new Error('audioFile is required');
  }

  const formData = new FormData();
  formData.append('file', audioFile);

  const { data } = await alkymindClient.post(CHAT_ASSISTANT_ENDPOINTS.TRANSCRIBE_AUDIO, formData, {
    headers: {
      'Content-Type': FORMDATA_HEADERS.MULTIPART_FORM_DATA,
    },
  });
  return data;
};
export const getChatAssistantDrafts = async (
  limit = CHAT_ASSISTANT_DEFAULTS.DRAFTS_LIMIT,
  offset = CHAT_ASSISTANT_DEFAULTS.DRAFTS_OFFSET
) => {
  const { data } = await screeningsClient.get(CHAT_ASSISTANT_ENDPOINTS.CHAT_ASSISTANT_DRAFTS, {
    params: {
      limit,
      offset,
    },
  });
  return data;
};

export const createChatAssistantDraft = async (workspaceId, threadId, name, messages = []) => {
  if (!workspaceId || !name) {
    throw new Error('workspaceId and name are required');
  }
  const { data } = await screeningsClient.post(CHAT_ASSISTANT_ENDPOINTS.CHAT_ASSISTANT_DRAFTS, {
    workspaceId,
    threadId,
    name,
    messages,
  });
  return data;
};

export const deleteChatAssistantDraft = async (draftId) => {
  try {
    const { data } = await screeningsClient.delete(
      `${CHAT_ASSISTANT_ENDPOINTS.CHAT_ASSISTANT_DRAFTS}/${draftId}`
    );
    return data;
  } catch (error) {
    if (error.response?.status === ERROR_STATUSES.NOT_FOUND) {
      return { success: true, message: CHAT_ASSISTANT_MESSAGES.DRAFT_ALREADY_DELETED };
    }
    throw error;
  }
};

export const updateChatAssistantDraftThreadId = async (draftId, threadId) => {
  if (!draftId || !threadId) {
    throw new Error('draftId and threadId are required');
  }

  const { data } = await screeningsClient.patch(
    `${CHAT_ASSISTANT_ENDPOINTS.CHAT_ASSISTANT_DRAFTS}/${draftId}/thread`,
    {
      threadId,
    }
  );
  return data;
};

export const getChatDraftWithMessages = async (draftId) => {
  if (!draftId) {
    throw new Error('draftId is required');
  }

  const { data } = await screeningsClient.get(
    `${CHAT_ASSISTANT_ENDPOINTS.CHAT_ASSISTANT_DRAFTS}/${draftId}/messages`
  );
  return data;
};

export const syncChatMessages = async (draftId) => {
  if (!draftId) {
    throw new Error('draftId is required');
  }

  const { data } = await screeningsClient.post(
    `${CHAT_ASSISTANT_ENDPOINTS.CHAT_ASSISTANT_DRAFTS}/${draftId}/sync`
  );
  return data;
};

export const updateChatAssistantDraft = async (draftId, updateData) => {
  if (!draftId || !updateData) {
    throw new Error('draftId and updateData are required');
  }

  const { data } = await screeningsClient.patch(
    `${CHAT_ASSISTANT_ENDPOINTS.CHAT_ASSISTANT_DRAFTS}/${draftId}/update`,
    updateData
  );
  return data;
};

export const updateChatAssistantDraftWithConfig = async (draftId, configData) => {
  if (!draftId || !configData) {
    throw new Error('draftId and configData are required');
  }
  const updateData = {};

  if (configData.name) updateData.name = configData.name;
  if (configData.language) updateData.language = configData.language;
  if (configData.duration) updateData.duration = configData.duration;
  if (configData.purpose) updateData.purpose = configData.purpose;
  if (configData.customPurpose) updateData.customPurpose = configData.customPurpose;

  if (configData.startDate) updateData.startDate = configData.startDate;
  if (configData.endDate) updateData.endDate = configData.endDate;
  if (configData.emptyDates !== undefined) updateData.emptyDates = configData.emptyDates;

  if (configData.customTestGroup) {
    const formattedCustomTestGroup = {
      ...configData.customTestGroup,
    };

    if (formattedCustomTestGroup.tests && Array.isArray(formattedCustomTestGroup.tests)) {
      if (
        formattedCustomTestGroup.tests.length > 0 &&
        typeof formattedCustomTestGroup.tests[0] === 'string'
      ) {
        formattedCustomTestGroup.tests = formattedCustomTestGroup.tests.map((testId) => ({
          testId,
        }));
      }
    }

    if (formattedCustomTestGroup.difficultyConfig) {
      if (formattedCustomTestGroup.difficultyConfig.difficultyConfig) {
        formattedCustomTestGroup.difficultyConfig =
          formattedCustomTestGroup.difficultyConfig.difficultyConfig;
      }

      if (Array.isArray(formattedCustomTestGroup.difficultyConfig)) {
        formattedCustomTestGroup.difficultyConfig = formattedCustomTestGroup.difficultyConfig.map(
          (item) => ({
            difficultyId: String(item.difficultyId),
            quantity: Number(item.quantity),
            order: Number(item.order || 0),
            theme: item.theme || null,
            listeningTest: Boolean(item.listeningTest || false),
          })
        );
      }
    }

    updateData.customTestGroup = formattedCustomTestGroup;
  }

  if (configData.difficultyConfig && !configData.customTestGroup) {
    updateData.customTestGroup = {
      difficultyConfig: configData.difficultyConfig,
    };
  }

  if (configData.subskills && configData.subskills.length > 0) {
    updateData.subskills = configData.subskills;
  }

  updateData.proctoringEnabled =
    configData.proctoringEnabled !== undefined
      ? configData.proctoringEnabled
      : CHAT_ASSISTANT_DEFAULTS.PROCTORING_ENABLED;
  updateData.proctoringAIEnabled =
    configData.proctoringAIEnabled !== undefined
      ? configData.proctoringAIEnabled
      : CHAT_ASSISTANT_DEFAULTS.PROCTORING_AI_ENABLED;
  updateData.allowCertificateDownload =
    configData.allowCertificateDownload !== undefined
      ? configData.allowCertificateDownload
      : CHAT_ASSISTANT_DEFAULTS.ALLOW_CERTIFICATE_DOWNLOAD;
  updateData.requireCompletedProfile =
    configData.requireCompletedProfile !== undefined
      ? configData.requireCompletedProfile
      : CHAT_ASSISTANT_DEFAULTS.REQUIRE_COMPLETED_PROFILE;

  const { data } = await screeningsClient.patch(`/recruitsessiondraft/${draftId}`, updateData);
  return data;
};

export const getSubskillsByIds = async (subskillIds) => {
  if (!subskillIds || !Array.isArray(subskillIds) || subskillIds.length === 0) {
    return [];
  }

  try {
    const { data } = await screeningsClient.post('/subskills/refresh', {
      ids: subskillIds,
    });
    return data;
  } catch (error) {
    console.error('Error fetching subskills:', error);
    return [];
  }
};

export const generateExampleQuestions = async (
  subskills,
  questionType = CHAT_ASSISTANT_DEFAULTS.QUESTION_TYPE,
  language = CHAT_ASSISTANT_DEFAULTS.LANGUAGE,
  context = {}
) => {
  if (!subskills || !Array.isArray(subskills) || subskills.length === 0) {
    throw new Error('subskills array is required and cannot be empty');
  }

  try {
    const skill = subskills[0];
    const roleContext = context.role
      ? t('CHAT_AI_QUESTION_TEMPLATE_FOR_ROLE').replace('{{role}}', context.role)
      : '';
    const difficultyContext = context.difficulty
      ? t('CHAT_AI_QUESTION_TEMPLATE_WITH_LEVEL').replace('{{difficulty}}', context.difficulty)
      : '';
    const purposeContext =
      context.purpose === 'RECRUITMENT' ? t('CHAT_AI_QUESTION_TEMPLATE_FOR_RECRUITMENT') : '';

    const baseQuestion = t('CHAT_AI_QUESTION_TEMPLATE_CREATE')
      .replace('{{count}}', CHAT_ASSISTANT_DEFAULTS.EXAMPLE_QUESTIONS_COUNT)
      .replace('{{skill}}', skill.name || skill);

    const requestData = {
      question: `${baseQuestion}${roleContext}${difficultyContext}${purposeContext}. ${
        skill.description || ''
      }`,
      answerType: questionType,
      language,
    };

    const response = await getAISuggestions(requestData);

    if (response.answers && response.answers.length > 0) {
      return response.answers.map((answer, index) => ({
        ...answer,
        skill: skill.name || skill,
        skillId: skill.id,
        index: index + 1,
        difficulty: context.difficulty || 'intermediate',
      }));
    }

    throw new Error('No questions generated');
  } catch (error) {
    return Array.from({ length: CHAT_ASSISTANT_DEFAULTS.EXAMPLE_QUESTIONS_COUNT }).map(
      (_, index) => ({
        id: `mock_q_${index}`,
        question: t('CHAT_AI_QUESTION_EXAMPLE_FALLBACK')
          .replace('{{number}}', index + 1)
          .replace('{{skill}}', subskills[0]?.name || subskills[0]),
        type: questionType,
        skill: subskills[0]?.name || subskills[0],
        skillId: subskills[0]?.id,
        index: index + 1,
        difficulty: context.difficulty || 'intermediate',
      })
    );
  }
};

// CV File services for prefilter questions
export const uploadCvFile = async (file, workspaceId) => {
  try {
    const formData = new FormData();
    formData.append('cv', file);
    formData.append('workspaceId', workspaceId);
    formData.append('type', 'prefilter_cv');

    const { data } = await screeningsClient.post('/files/upload/cv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return {
      success: true,
      fileId: data.fileId,
      fileName: data.fileName,
      fileUrl: data.fileUrl,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Error uploading CV file',
    };
  }
};

export const getCandidateExistingCv = async (candidateId) => {
  try {
    const { data } = await alkymetricsClient.get(`/profiles/${candidateId}/cv`);
    return data;
  } catch (error) {
    return null;
  }
};

export const submitCvFileAnswer = async (screeningInstanceId, questionId, answerData) => {
  try {
    const { data } = await screeningsClient.post(
      `/screenings/instance/${screeningInstanceId}/answers/cv-file`,
      {
        questionId,
        ...answerData,
      }
    );
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error submitting CV file answer');
  }
};

export const processCvFileWithAI = async (fileId, instructions) => {
  try {
    const { data } = await alkymindClient.post('/cv-analysis', {
      fileId,
      instructions,
    });
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error processing CV with AI');
  }
};

export const downloadCvTemplate = async () => {
  try {
    const { data } = await screeningsClient.get('/templates/cv-template', {
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    throw new Error('Error downloading CV template');
  }
};

// Dismissed candidates control services
export const getDismissedCandidatesPending = async (screeningId, filters = {}) => {
  try {
    const { data } = await screeningsClient.get(
      `/screenings/${screeningId}/dismissed-candidates/pending`,
      {
        params: filters,
      }
    );
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error fetching dismissed candidates');
  }
};

export const cancelCandidateDismissal = async (candidateId) => {
  try {
    const { data } = await screeningsClient.post(`/candidates/${candidateId}/dismissal/cancel`);
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error cancelling candidate dismissal');
  }
};

export const sendCandidateInvitation = async (candidateId, screeningId) => {
  try {
    const { data } = await screeningsClient.post(`/candidates/${candidateId}/invitation/send`, {
      screeningId,
    });
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error sending candidate invitation');
  }
};

export const getDismissalDetails = async (candidateId) => {
  try {
    const { data } = await screeningsClient.get(`/candidates/${candidateId}/dismissal/details`);
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error fetching dismissal details');
  }
};

export const updateDismissalStatus = async (candidateId, status, comment = null) => {
  try {
    const { data } = await screeningsClient.patch(`/candidates/${candidateId}/dismissal/status`, {
      status, // 'cancelled', 'confirmed', 'pending'
      comment,
    });
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error updating dismissal status');
  }
};

export const getCampusBlockedCandidates = async (screeningId) => {
  try {
    const { data } = await screeningsClient.get(
      `/screenings/${screeningId}/campus/blocked-candidates`
    );
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error fetching campus blocked candidates');
  }
};

export const unblockCampusCandidate = async (candidateId, reason = '') => {
  try {
    const { data } = await screeningsClient.post(`/candidates/${candidateId}/campus/unblock`, {
      reason,
    });
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error unblocking campus candidate');
  }
};

export const getTestsBySubskills = async (subskillIds, limit = 30, companyId = null) => {
  try {
    const body = {
      subskillIds,
      ...(limit && { limit }),
      ...(companyId && { companyId }),
    };

    const { data } = await screeningsClient.post('/tests/bySubskills', body);
    return data;
  } catch (error) {
    console.error('Error fetching tests by subskills:', error);
    return {
      totalTests: 0,
      subskillIds: [],
      testIds: [],
      difficulties: [],
      testsByDifficulty: {},
      summary: {
        totalAvailable: 0,
        difficultiesWithTests: 0,
      },
    };
  }
};
